/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectNotification } from 'modules/common/notifications/selectors';
import { useEffect, useMemo } from 'react';
import { notificationActions } from 'modules/common/notifications/slice';
import { isEmpty } from 'modules/common/helpers/object';
import { llmActions } from '../../slice';
import { selectAllModels, selectLoader, selectLlmDefaultModels } from '../../selectors';
import { LLM_PROVIDERS, LLM_FEATURES } from '../../constants';
import { Alert, TextField } from './style';
import { updateModelValidation } from '../../validation/update-model-validation';

/**
 * Modal implementation to update model
 * @returns
 */
const UpdateModelForm = ({ open, onClose }) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const loading = useSelector(selectLoader);
  const notification = useSelector(selectNotification);
  const systemDefaultModels = useSelector(selectLlmDefaultModels);
  const allModels = useSelector(selectAllModels);

  // Models are identified by provider + model, since model names can repeat across providers
  const modelKey = (provider, model) => (provider || model ? `${provider}/${model}` : '');
  const providerLabel = (provider) =>
    LLM_PROVIDERS.find((p) => p.key === provider)?.label ?? provider;

  const modalAction = (action) => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    onClose(action);
  };

  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      modalAction(false);
    }
  }, [notification]);

  const onSubmitUpdateModel = async (values) => {
    const valueObj = {
      key: 'llmDefaultModels',
      value: LLM_FEATURES.reduce((acc, feature) => {
        acc[feature.key] = {
          provider: values[`${feature.key}-provider`],
          model: values[`${feature.key}-model`],
        };
        return acc;
      }, {}),
    };

    if (isEmpty(systemDefaultModels)) {
      dispatch(llmActions.createDefaultModelsInformation(valueObj));
    } else {
      dispatch(
        llmActions.updateDefaultModelsInformation({
          systemInformationId: systemDefaultModels?.id,
          ...valueObj,
        })
      );
    }
  };
  //

  // Prefill the form with the saved default models, falling back to empty values
  const initialValues = useMemo(
    () =>
      LLM_FEATURES.reduce(
        (acc, feature) => ({
          ...acc,
          [`${feature.key}-provider`]: systemDefaultModels?.value?.[feature.key]?.provider || '',
          [`${feature.key}-model`]: systemDefaultModels?.value?.[feature.key]?.model || '',
        }),
        {}
      ),
    [systemDefaultModels]
  );
  console.log(systemDefaultModels, initialValues)
  return (
    <Dialog
      open={open}
      onClose={() => modalAction(false)}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">Update Model</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={updateModelValidation}
          onSubmit={onSubmitUpdateModel}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            touched,
            values,
          }) => 
            //
             (
              <form noValidate onSubmit={handleSubmit}>
                {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                  <Alert mt={2} mb={3} severity={notification?.type}>
                    {notification?.message}
                  </Alert>
                )}
                {LLM_FEATURES.map((feature) => (
                  <Box key={feature.key}>
                    <Typography>{feature.label}</Typography>
                    <Stack direction="row" spacing={2} sx={{ marginY: 2 }}>
                      <TextField
                        size="small"
                        select
                        name={`${feature.key}-model`}
                        value={modelKey(
                          values[`${feature.key}-provider`],
                          values[`${feature.key}-model`]
                        )}
                        label="Model"
                        onChange={(event) => {
                          // Find the matching object from allModels
                          const selectedModelObj = allModels?.results?.find(
                            (m) => modelKey(m.provider, m.model) === event.target.value
                          );
                          if (!selectedModelObj) return;

                          // Update the model and its provider together
                          setFieldValue(`${feature.key}-model`, selectedModelObj.model);
                          setFieldValue(`${feature.key}-provider`, selectedModelObj.provider);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(
                          touched[`${feature.key}-model`] && errors[`${feature.key}-model`]
                        )}
                        helperText={
                          touched[`${feature.key}-model`] && errors[`${feature.key}-model`]
                        }
                        disabled={loading}
                        fullWidth
                        sx={{ marginY: 2 }}
                        InputLabelProps={{ shrink: true }}
                      >
                        {allModels?.results?.map((type) => (
                          <MenuItem
                            key={modelKey(type.provider, type.model)}
                            value={modelKey(type.provider, type.model)}
                          >
                            {type.name || `${type.model} (${providerLabel(type.provider)})`}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        // name="model"
                        size="small"
                        name={`${feature.key}-provider`}
                        value={values[`${feature.key}-provider`]}
                        error={Boolean(
                          touched[`${feature.key}-provider`] && errors[`${feature.key}-provider`]
                        )}
                        helperText={
                          touched[`${feature.key}-provider`] && errors[`${feature.key}-provider`]
                        }
                        disabled
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        my={2}
                        label="Provider"
                      />
                    </Stack>
                  </Box>
                ))}

                <DialogActions>
                  <Button
                    onClick={() => modalAction(false)}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={isSubmitting || loading || !organizationId}
                  >
                    Update Model
                  </Button>
                </DialogActions>
              </form>
            )
          }
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
//
export default UpdateModelForm;
